
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Poll, PollStatus } from '@/types/poll';
import { useNotificationDispatcher } from '@/hooks/useNotificationDispatcher';
import { useEmailRecipients } from '../notifications/useEmailRecipients';

export const usePollActivation = (
  polls: Poll[], 
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>
) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { dispatch } = useNotificationDispatcher();
  const { fetchRecipientEmails } = useEmailRecipients();

  const handleActivatePoll = async (id: string) => {
    try {
      setIsProcessing(true);
      const { error } = await supabase
        .from('polls')
        .update({ status: 'live' })
        .eq('id', id);
      
      if (error) {
        toast.error('Failed to activate poll');
        return;
      }
      
      // Update local poll state
      const updatedPolls = polls.map(poll => 
        poll.id === id ? { ...poll, status: 'live' as PollStatus } : poll
      );
      setPolls(updatedPolls);

      // Send notification for new poll
      const activatedPoll = updatedPolls.find(p => p.id === id);
      if (activatedPoll) {
        console.log('Sending new poll notification for:', activatedPoll.title);
        
        const verifiedPollsterEmails = await fetchRecipientEmails('verified_pollsters');
        
        try {
          const notificationSent = await dispatch('newPollPublished', verifiedPollsterEmails, {
            pollData: activatedPoll
          });
          
          if (notificationSent) {
            console.log('New poll notification sent successfully');
          } else {
            console.warn('New poll notification might not have been sent');
          }
        } catch (notificationError) {
          console.error('Error sending new poll notification:', notificationError);
        }
      }
      
      toast.success('Poll activated');
    } catch (error) {
      console.error('Error activating poll:', error);
      toast.error('An error occurred while activating the poll');
    } finally {
      setIsProcessing(false);
    }
  };

  return { handleActivatePoll, isProcessing };
};
